// @ts-nocheck
import React from 'react';
import { ChevronRight, Lock, Unlock } from 'lucide-react';
import type { AnalysisRunRow } from '../../services/FirmCompaniesApiClient';
import type { AnalysisWorkflowProgress } from '../../services/AnalysisApiClient';
import {
  RunHistoryRunsTable,
  mapFirmCompanyRunHistoryToTableRows,
  type RunHistoryTableRow,
} from '../client/runHistory';
import { isAnalysisLocked } from '../../constants/analysisStatus';
import { firmLiveAnalysisWorkflowStatusChipClass } from './firmLiveAnalysisTableUtils';

export function FirmLiveAnalysisExpandChevron({
  canExpand,
  isExpanded,
  onToggle,
  ariaControlsId,
}: {
  canExpand: boolean;
  isExpanded: boolean;
  onToggle: (e: React.MouseEvent) => void;
  ariaControlsId: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle(e);
      }}
      aria-expanded={isExpanded}
      aria-controls={ariaControlsId}
      disabled={!canExpand}
      title={canExpand ? (isExpanded ? 'Hide run history' : 'Show run history') : undefined}
      className={[
        'mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md',
        'transition-colors duration-150',
        canExpand ? 'text-gray-400 hover:bg-gray-100 hover:text-gray-700' : 'text-gray-300 cursor-not-allowed',
      ].join(' ')}
    >
      <ChevronRight
        size={16}
        aria-hidden
        className={`transition-transform duration-150 ${isExpanded ? 'rotate-90 text-emerald-700' : ''}`}
      />
    </button>
  );
}

export function FirmAnalysisPersistedLockMark({
  analysisPersistedStatus,
  iconSize = 13,
  withAriaLabel = false,
}: {
  analysisPersistedStatus?: string | null;
  iconSize?: number;
  withAriaLabel?: boolean;
}) {
  const locked = isAnalysisLocked(analysisPersistedStatus);
  return (
    <span
      className={`inline-flex shrink-0 items-center ${locked ? 'text-red-600' : 'text-gray-400'}`}
      title={
        locked
          ? 'Final: workflow edits and new runs are disabled in the client app.'
          : 'Editable: analysis is not final.'
      }
      {...(withAriaLabel
        ? {
            'aria-label': locked ? 'Final analysis' : 'Editable analysis',
          }
        : { 'aria-hidden': true })}
    >
      {locked ? <Lock size={iconSize} className="shrink-0" aria-hidden /> : <Unlock size={iconSize} aria-hidden />}
    </span>
  );
}

/** Mirrors client Analyses list: `N%` + Complete / In progress / Not started (tooltip: steps). */
export function FirmLiveAnalysisWorkflowStatusBadge({
  workflowProgress,
  statusLabel,
}: {
  workflowProgress?: AnalysisWorkflowProgress | null;
  /** Fallback when API omits workflowProgress. */
  statusLabel: string;
}) {
  const wp = workflowProgress;
  if (wp) {
    const status = wp.status === 'complete' ? 'Complete' : wp.status === 'not_started' ? 'Not started' : 'In progress';
    const statusClass =
      status === 'Complete'
        ? 'bg-emerald-100/80 backdrop-blur-sm border border-emerald-200/60 text-emerald-800'
        : status === 'In progress'
          ? 'bg-amber-100/80 backdrop-blur-sm border border-amber-200/60 text-amber-800'
          : 'bg-gray-100/80 backdrop-blur-sm border border-gray-200/60 text-gray-600';
    return (
      <span
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium shadow-sm ${statusClass}`}
        title={`${wp.stepsCompleted} of ${wp.totalSteps} workflow steps complete`}
      >
        <span className="font-semibold tabular-nums">{wp.percent}%</span>
        <span className="opacity-90">{status}</span>
      </span>
    );
  }

  const label = String(statusLabel ?? '').trim() || 'Not started';
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium shadow-sm ${firmLiveAnalysisWorkflowStatusChipClass(label)}`}
    >
      {label}
    </span>
  );
}

/**
 * Expanded “run history” block under a live analysis row (company detail + firm dashboard).
 */
export function FirmLiveAnalysisRunHistoryPanel({
  panelId,
  title,
  legacyRuns,
  onViewSnapshot,
  emptyHint = 'No legacy run history.',
  className = '',
}: {
  panelId: string;
  title: string;
  legacyRuns: AnalysisRunRow[];
  onViewSnapshot: (row: RunHistoryTableRow) => void;
  emptyHint?: string;
  /** Merged onto the outer panel wrapper (e.g. `cursor-default` on firm dashboard expand rows). */
  className?: string;
}) {
  const count = legacyRuns.length;
  return (
    <div
      id={panelId}
      className={['rounded-lg border border-gray-200 bg-white px-4 py-3', className].filter(Boolean).join(' ')}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <span className="text-xs text-gray-500 tabular-nums">
          {count} run{count === 1 ? '' : 's'}
        </span>
      </div>
      {count === 0 ? (
        <p className="text-sm text-gray-500">{emptyHint}</p>
      ) : (
        <RunHistoryRunsTable
          variant="embedded"
          hideAnalysisColumn
          rows={mapFirmCompanyRunHistoryToTableRows(legacyRuns)}
          onViewSnapshot={onViewSnapshot}
        />
      )}
    </div>
  );
}
